import React, { useState } from 'react';
import { X } from 'lucide-react';
import type { Crcy } from '../../types/Crcy';
import type { ForwardInterestSettingNew } from '../../types/ForwardInterestSetting';
import { t } from '../../i18n/t';
import { LopButton } from '../../components/LopButton';
import './AddForwardInterestSettingModal.css';

export interface AddForwardInterestSettingModalProps {
  crcies: Crcy[];
  submitting: boolean;
  // Server-side error from a failed submit (e.g. duplicate ticker), shown
  // alongside client-side validation errors. Distinct from local `error`
  // state below, which only covers required-field checks.
  submitError: string | null;
  onSubmit: (payload: ForwardInterestSettingNew) => void;
  onClose: () => void;
}

// Wizard for creating a new "Bloomberg forward point" row. Unlike the grid's
// inline-edit columns, this collects the FULL set of fields — including the
// ticker, which lives on the underlying ASSETS row the server creates
// atomically alongside this settings row. There is no assetId field here:
// the server assigns it.
export function AddForwardInterestSettingModal({
  crcies,
  submitting,
  submitError,
  onSubmit,
  onClose,
}: AddForwardInterestSettingModalProps) {
  const [assetName, setAssetName] = useState('');
  const [ticker, setTicker] = useState('');
  const [days, setDays] = useState('');
  const [period, setPeriod] = useState('');
  const [ledCrcyId, setLedCrcyId] = useState<number | ''>('');
  const [leaderCrcyId, setLeaderCrcyId] = useState<number | ''>('');
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = () => {
    const daysNum = Number(days);
    if (
      !assetName.trim() ||
      !ticker.trim() ||
      days === '' ||
      Number.isNaN(daysNum) ||
      ledCrcyId === '' ||
      leaderCrcyId === ''
    ) {
      setError(t('forwardInterestSettings.wizard.validation'));
      return;
    }
    setError(null);
    onSubmit({
      assetName: assetName.trim(),
      ticker: ticker.trim(),
      days: daysNum,
      period: period.trim() ? period.trim() : null,
      ledCrcyId,
      leaderCrcyId,
    });
  };

  return (
    <div
      className="fisw__backdrop"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        className="fisw"
        role="dialog"
        aria-modal="true"
        aria-label={t('forwardInterestSettings.wizard.title')}
      >
        <header className="fisw__header">
          <h2 className="fisw__title">{t('forwardInterestSettings.wizard.title')}</h2>
          <button
            type="button"
            className="fisw__close"
            aria-label={t('recordForm.close')}
            onClick={onClose}
          >
            <X size={18} />
          </button>
        </header>

        <div className="fisw__body">
          <label className="fisw__field">
            <span>{t('forwardInterestSettings.columns.assetName')}</span>
            <input
              type="text"
              value={assetName}
              onChange={(e) => setAssetName(e.target.value)}
            />
          </label>
          <label className="fisw__field">
            <span>{t('forwardInterestSettings.columns.ticker')}</span>
            <input type="text" value={ticker} onChange={(e) => setTicker(e.target.value)} />
          </label>
          <label className="fisw__field">
            <span>{t('forwardInterestSettings.columns.days')}</span>
            <input type="number" value={days} onChange={(e) => setDays(e.target.value)} />
          </label>
          <label className="fisw__field">
            <span>{t('forwardInterestSettings.columns.period')}</span>
            <input type="text" value={period} onChange={(e) => setPeriod(e.target.value)} />
          </label>
          <label className="fisw__field">
            <span>{t('forwardInterestSettings.columns.ledCrcyId')}</span>
            <select
              value={ledCrcyId}
              onChange={(e) => setLedCrcyId(e.target.value === '' ? '' : Number(e.target.value))}
            >
              <option value="">—</option>
              {crcies.map((c) => (
                <option key={c.crcyId} value={c.crcyId}>
                  {c.crcyId} - {c.crcyName}
                </option>
              ))}
            </select>
          </label>
          <label className="fisw__field">
            <span>{t('forwardInterestSettings.columns.leaderCrcyId')}</span>
            <select
              value={leaderCrcyId}
              onChange={(e) =>
                setLeaderCrcyId(e.target.value === '' ? '' : Number(e.target.value))
              }
            >
              <option value="">—</option>
              {crcies.map((c) => (
                <option key={c.crcyId} value={c.crcyId}>
                  {c.crcyId} - {c.crcyName}
                </option>
              ))}
            </select>
          </label>
        </div>

        {(error || submitError) && (
          <div className="fisw__error">{error ?? submitError}</div>
        )}

        <footer className="fisw__footer">
          <LopButton variant="primary" onClick={handleSubmit} loading={submitting}>
            {t('forwardInterestSettings.wizard.submit')}
          </LopButton>
          <LopButton variant="secondary" onClick={onClose} disabled={submitting}>
            {t('recordForm.cancel')}
          </LopButton>
        </footer>
      </div>
    </div>
  );
}
